import React, { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import CallHelpCard from './CallCard';

const formatAirlineName = (slug) => {
  return slug
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const AirlineDetail = () => {
  const { slug } = useParams();
  const airlineName = formatAirlineName(slug);

  const sectionRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("fade-visible");
        });
      },
      { threshold: 0.2 }
    );

    sectionRefs.current.forEach((el) => observer.observe(el));
  }, []);

  return (
    <div className="bg-[#F9FAF9] min-h-screen pb-12 px-4">
      <div className="bg-white rounded-3xl max-w-4xl mx-auto px-6 py-10 shadow-xl border border-gray-200 fade-section">

        <CallHelpCard />

        <h1 className="text-3xl sm:text-4xl font-extrabold text-teal-700 mb-6 fade-section">
          About {airlineName}
        </h1>

        {/* SECTIONS */}
        {[
          {
            title: "",
            content: `${airlineName} is a low-cost airline known for affordable fares...`,
          },
          {
            title: "Cabin Classes",
            content: (
              <ul className="list-disc ml-5">
                <li>Basic Economic Class</li>
                <li>Premium Economic Class</li>
                <li>Business Class (Business Flex)</li>
              </ul>
            ),
          },
          {
            title: "Services & Facilities",
            content: (
              <ul className="list-disc ml-5">
                <li>Multiple flight packages</li>
                <li>Hotels, cars, gift cards</li>
                <li>Frequent flyer program</li>
                <li>Entertainment & inflight meals</li>
                <li>Online booking & check-in</li>
              </ul>
            ),
          },
          {
            title: "Main Hub",
            content:
              "Main hub: Istanbul Sabiha Gökçen International Airport (SAW). Secondary hubs include Berlin, Ankara, Doha, and more.",
          },
          {
            title: "Top International Destinations",
            content: (
              <ul className="list-disc ml-5">
                <li>Istanbul, Munich, Berlin</li>
                <li>Amsterdam, London, Dubai</li>
                <li>Tbilisi, Moscow, Antalya</li>
                <li>Tirana, Yerevan, Baku, Graz</li>
              </ul>
            ),
          },
          {
            title: "Code-Share Partners",
            content: (
              <ul className="list-disc ml-5">
                <li>Delta Air Lines</li>
                <li>KLM Airlines</li>
                <li>Qatar Airways</li>
                <li>ITA Airways</li>
              </ul>
            ),
          },
          {
            title: "Booking Process",
            content: (
              <ol className="list-decimal ml-5">
                <li>Visit booking portal</li>
                <li>Enter travel details</li>
                <li>Select flight & add-ons</li>
                <li>Fill traveler info</li>
                <li>Pay & receive confirmation</li>
              </ol>
            ),
          },
          {
            title: "Change / Cancellation",
            content: (
              <ul className="list-disc ml-5">
                <li>Changes available online</li>
                <li>Free change within 24 hours</li>
                <li>Airline fees may apply</li>
                <li>Refunds in 7–10 days</li>
              </ul>
            ),
          },
          {
            title: "Loyalty Program",
            content: (
              <>
                <p>
                  Earn and redeem points for flights, seats, baggage, and more.
                  Create an account online.
                </p>
                <ul className="list-disc ml-5 mt-2">
                  <li>Redeem for taxes, upgrades</li>
                  <li>Manage account on mobile app</li>
                </ul>
              </>
            ),
          },
          {
            title: "Refund Policy",
            content: (
              <ul className="list-disc ml-5">
                <li>24-hour free cancellation</li>
                <li>Refunds processed to original payment</li>
                <li>Full refund for airline-caused flight cancellations</li>
              </ul>
            ),
          },
          {
            title: "Seat Upgrade",
            content: (
              <ol className="list-decimal ml-5">
                <li>Go to Manage Booking</li>
                <li>Enter PNR & last name</li>
                <li>Choose & pay for upgraded seat</li>
              </ol>
            ),
          },
          {
            title: "Pet Policy",
            content: (
              <ul className="list-disc ml-5">
                <li>Pets allowed: dogs, cats, birds</li>
                <li>Max weight: 8 kg incl. carrier</li>
                <li>Carrier size: 32×32×50 cm</li>
                <li>Health certificate required</li>
              </ul>
            ),
          },
          {
            title: "Name & DOB Change",
            content: (
              <ul className="list-disc ml-5">
                <li>Minor name fixes allowed</li>
                <li>DOB cannot be changed</li>
                <li>Major name error requires new ticket</li>
              </ul>
            ),
          },
          {
            title: "Group Booking",
            content: (
              <ul className="list-disc ml-5">
                <li>Groups of 10–100 allowed</li>
                <li>Names can be changed 48 hours prior</li>
              </ul>
            ),
          },
          {
            title: "Discounts & Offers",
            content: (
              <ul className="list-disc ml-5">
                <li>Member discounts</li>
                <li>Festival seasonal offers</li>
                <li>Student concessions</li>
                <li>Last-minute deals</li>
              </ul>
            ),
          },
          {
            title: "International Travel Rules",
            content: (
              <ul className="list-disc ml-5">
                <li>Valid passport + visa</li>
                <li>Arrive 3 hours early</li>
                <li>Follow baggage limits</li>
              </ul>
            ),
          },
          {
            title: "Business Class Features",
            content: (
              <ul className="list-disc ml-5">
                <li>20 kg free baggage</li>
                <li>Recliner seats</li>
                <li>Complimentary meals</li>
                <li>Entertainment system</li>
              </ul>
            ),
          },
        ].map((sec, idx) => (
          <section
            key={idx}
            ref={(el) => (sectionRefs.current[idx] = el)}
            className="mb-10 fade-section"
          >
            {sec.title && (
              <h2 className="text-2xl font-bold mb-3 text-teal-700">
                {sec.title}
              </h2>
            )}
            <div className="text-gray-700 leading-relaxed">{sec.content}</div>
          </section>
        ))}
      </div>
    </div>
  );
};

export default AirlineDetail;
